import { useState } from "react";

import { Send as SendIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";

interface QuestionFormProps {
  onSubmitQuestion: (question: string) => Promise<void>;
  error: string | null;
}

export function QuestionForm({ onSubmitQuestion, error }: QuestionFormProps) {
  const [newQuestion, setNewQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSendQuestion = async () => {
    if (!newQuestion.trim() || submitting) return;

    setSubmitting(true);

    try {
      await onSubmitQuestion(newQuestion);
      setNewQuestion("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && event.ctrlKey) {
      handleSendQuestion();
    }
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        px: 3,
        py: 2,
        backgroundColor: "grey.50",
        flexShrink: 0,
      }}
    >
      <Box mb={2}>
        <Typography
          variant="body2"
          fontWeight="semibold"
          color="text.primary"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.5,
            mb: 1,
            display: "block",
          }}
        >
          Ask a New Question
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Type your question here..."
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          onKeyPress={handleKeyPress}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "background.paper",
              fontSize: "16px",
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
              },
            },
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Questions are visible to the carrier only
        </Typography>
        <Button
          variant="contained"
          onClick={handleSendQuestion}
          disabled={!newQuestion.trim() || submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          sx={{
            px: 3,
            py: 1,
            fontWeight: "semibold",
            textTransform: "none",
          }}
        >
          {submitting ? "Sending..." : "Send Question"}
        </Button>
      </Stack>
      {error && (
        <Box mt={1}>
          <ErrorTypography error={error} />
        </Box>
      )}
    </Box>
  );
}
